import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, type ErrorComponentProps, Link, Outlet } from '@tanstack/react-router'
import { CompassIcon, TriangleAlertIcon } from 'lucide-react'
import { Toaster } from 'sonner'
import { MotionProvider } from '@/components/motion'
import { Button } from '@/components/ui/button'
import { ConfirmHost } from '@/components/ui/confirm'
import { CopyFallbackHost } from '@/components/ui/copy-fallback'
import { TooltipProvider } from '@/components/ui/misc'
import { useTheme } from '@/lib/theme'

export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
})

function RootLayout() {
  const theme = useTheme((s) => s.resolved)
  return (
    <MotionProvider>
      <TooltipProvider delayDuration={250} skipDelayDuration={100}>
        <Outlet />
        <ConfirmHost />
        <CopyFallbackHost />
        <Toaster
          theme={theme}
          position="bottom-right"
          richColors
          closeButton
          toastOptions={{ className: 'font-sans! rounded-xl! shadow-elevation-3!' }}
        />
      </TooltipProvider>
    </MotionProvider>
  )
}

function RouteError({ reset }: ErrorComponentProps) {
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl bg-destructive-soft text-destructive">
        <TriangleAlertIcon className="size-6" />
      </div>
      <div>
        <h1 className="font-semibold text-xl">Algo salió mal</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          No pudimos mostrar esta pantalla. Reintentá o volvé al inicio.
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={reset}>
          Reintentar
        </Button>
        <Button asChild>
          <Link to="/">Volver al inicio</Link>
        </Button>
      </div>
    </div>
  )
}

function NotFound() {
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
        <CompassIcon className="size-6" />
      </div>
      <div>
        <h1 className="font-semibold text-xl">Página no encontrada</h1>
        <p className="mt-1 text-muted-foreground text-sm">La dirección que buscás no existe o fue movida.</p>
      </div>
      <Button asChild variant="outline">
        <Link to="/">Volver al inicio</Link>
      </Button>
    </div>
  )
}
